"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";
import { NotificationAvatar } from "@/components/notifications/notification-avatar";
import { useProgressNavigation } from "@/components/progress-bar";
import { useUser } from "@/contexts/user-provider";
import { alertTypeLabel } from "@/lib/alert-type-label";
import { closeAllDesktopNotifications, fireDesktopNotification } from "@/lib/fire-desktop-notification";
import { getRouteForReference, renderPlainText, teamConversationIdFrom } from "@/lib/notification-reference";
import { sonnerToast, toast } from "@/lib/use-toast";
import { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import type { TeamMessageAlertPayload, TeamMessageSummary } from "@/types/team-chat";
import { useFaviconNotificationBadge } from "./use-favicon-notification-badge";
import { isInPersonLocked, useInPersonLocked } from "./use-in-person-form-lock";
import { useNotificationSound } from "./use-notification-sound";
import { ALL_ALERT_TYPES, useWebSocketCallbackSubscription } from "./use-websocket-subscription";
import { appendNotificationToCache } from "@/query/notifications/notifications";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";

/**
 * How many toasts sonner keeps visible; the rest of the stack renders at
 * opacity 0. Toasts with no timer have to stay within it or they wedge it.
 */
const MAX_PERSISTENT_TOASTS = 3;

// Exported for testing
export function formatAlertForToast(alert: AlertDetail): {
  title: string;
  description: string;
} {
  // A chat message is the one alert type whose subject is a person, so the
  // generic "<Type> Received" title wastes the line that should say who wrote
  // it — and the content line then repeats the type anyway. Name the sender and
  // let the description be the message.
  if (alert.type === AlertType.TEAM_MESSAGE_RECEIVED) {
    const message = alert.data as Partial<TeamMessageAlertPayload> | undefined;
    const sender = message?.sender;
    const senderName = `${sender?.firstName ?? ""} ${sender?.lastName ?? ""}`.trim();
    if (senderName) {
      const body = message?.content?.trim() || "Sent an attachment";
      // In a group the sender is not the subject — the thread is. Two groups
      // both titled by whoever happened to write are indistinguishable, so the
      // header names the group and the sender moves down onto the message line.
      // The title rides the payload: the console's conversation list is one
      // page deep, so a group below that line has nothing cached to read.
      if (message?.conversationType === "GROUP" && message.conversationTitle) {
        return {
          title: message.conversationTitle,
          description: `${senderName}: ${body}`
        };
      }
      return {
        title: senderName,
        description: body
      };
    }
  }
  const plainContent = renderPlainText(alert.content);
  return {
    title: alertTypeLabel(alert.type),
    description: plainContent
  };
}
export function useInAppNotifications() {
  const $ = _c(13);
  const {
    currentUser
  } = useUser();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    playNotificationSound
  } = useNotificationSound();
  const {
    onNotificationReceived
  } = useFaviconNotificationBadge();
  const currentUserRef = useRef(currentUser);
  const playNotificationSoundRef = useRef(playNotificationSound);
  const onNotificationReceivedRef = useRef(onNotificationReceived);
  const navigateWithProgressRef = useRef(navigateWithProgress);
  let t0;
  if ($[0] !== currentUser || $[1] !== navigateWithProgress || $[2] !== onNotificationReceived || $[3] !== playNotificationSound) {
    t0 = () => {
      currentUserRef.current = currentUser;
      playNotificationSoundRef.current = playNotificationSound;
      onNotificationReceivedRef.current = onNotificationReceived;
      navigateWithProgressRef.current = navigateWithProgress;
    };
    $[0] = currentUser;
    $[1] = navigateWithProgress;
    $[2] = onNotificationReceived;
    $[3] = playNotificationSound;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  useEffect(t0);
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const conversationToastsRef = useRef(t1);
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const persistentToastIdsRef = useRef(t2);
  let t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const toasts = conversationToastsRef.current;
      const unsubscribe = useTeamChatDrawerStore.subscribe(state => {
        if (!state.isOpen || !state.activeConversationId) {
          return;
        }
        const toastId = toasts.get(state.activeConversationId);
        if (toastId) {
          sonnerToast.dismiss(toastId);
          toasts.delete(state.activeConversationId);
          persistentToastIdsRef.current.delete(toastId);
        }
      });
      return () => {
        unsubscribe();
        persistentToastIdsRef.current.forEach(_temp);
        persistentToastIdsRef.current.clear();
        toasts.clear();
        closeAllDesktopNotifications();
      };
    };
    t4 = [];
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  const inPersonLocked = useInPersonLocked();
  let t5;
  let t6;
  if ($[9] !== inPersonLocked) {
    t5 = () => {
      if (!inPersonLocked) {
        return;
      }
      sonnerToast.dismiss();
      closeAllDesktopNotifications();
      conversationToastsRef.current.clear();
      persistentToastIdsRef.current.clear();
    };
    t6 = [inPersonLocked];
    $[9] = inPersonLocked;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = latestMessage => {
      if (!currentUserRef.current) {
        return;
      }
      if (!latestMessage.notification) {
        return;
      }
      if (!appendNotificationToCache(latestMessage)) {
        return;
      }
      if (isInPersonLocked()) {
        return;
      }
      onNotificationReceivedRef.current();
      playNotificationSoundRef.current();
      const {
        title,
        description
      } = formatAlertForToast(latestMessage);
      const conversationId = latestMessage.notification?.alert?.communicationConversationId;
      const conversationRoute = conversationId ? getRouteForReference("socialconversation", conversationId) : null;
      const teamConversationId = teamConversationIdFrom(latestMessage.content);
      const teamSender = teamConversationId ? (latestMessage.data as Partial<TeamMessageSummary> | undefined)?.sender : undefined;
      const action = teamConversationId ? {
        label: "View",
        altText: "Open conversation in Whisper",
        onClick: () => {
          const store = useTeamChatDrawerStore.getState();
          if (!store.isOpen) {
            store.openDrawer();
          }
          store.setActiveConversation(teamConversationId);
        }
      } : conversationRoute ? {
        label: "View",
        altText: "View social message",
        onClick: () => {
          navigateWithProgressRef.current(conversationRoute);
        }
      } : latestMessage.patientId ? {
        label: "View",
        altText: "View patient chart",
        onClick: () => {
          navigateWithProgressRef.current(`/patient/${latestMessage.patientId}`);
        }
      } : undefined;
      const isPersistent = latestMessage.notification.persistent === true;
      const stackKey = teamConversationId ?? latestMessage.notification.groupKey ?? null;
      fireDesktopNotification({
        title,
        body: description,
        tag: stackKey ?? latestMessage.id,
        requireInteraction: isPersistent,
        onActivate: action?.onClick
      });
      const drawer = useTeamChatDrawerStore.getState();
      if (teamConversationId && drawer.isOpen && drawer.activeConversationId === teamConversationId) {
        return;
      }
      const supersededToastId = stackKey ? conversationToastsRef.current.get(stackKey) : undefined;
      if (supersededToastId && stackKey) {
        sonnerToast.dismiss(supersededToastId);
        conversationToastsRef.current.delete(stackKey);
        persistentToastIdsRef.current.delete(supersededToastId);
      }
      if (isPersistent) {
        const liveIds = persistentToastIdsRef.current;
        while (liveIds.size >= MAX_PERSISTENT_TOASTS) {
          const oldestId = liveIds.values().next().value as string;
          liveIds.delete(oldestId);
          sonnerToast.dismiss(oldestId);
          conversationToastsRef.current.forEach((id_0, conversation) => {
            if (id_0 === oldestId) {
              conversationToastsRef.current.delete(conversation);
            }
          });
        }
      }
      const toastId_0 = latestMessage.id;
      const forgetToast = () => {
        persistentToastIdsRef.current.delete(toastId_0);
        if (stackKey && conversationToastsRef.current.get(stackKey) === toastId_0) {
          conversationToastsRef.current.delete(stackKey);
        }
      };
      const senderAvatar = teamConversationId ? <NotificationAvatar providerId={teamSender?.providerId} firstName={teamSender?.firstName} lastName={teamSender?.lastName} /> : undefined;
      toast({
        id: toastId_0,
        title,
        description,
        variant: "info",
        action: action && {
          ...action,
          onClick: () => {
            forgetToast();
            return action.onClick();
          }
        },
        onDismiss: forgetToast,
        ...(senderAvatar ? {
          icon: senderAvatar,
          classNames: {
            icon: "!h-8 !w-8"
          }
        } : {}),
        ...(isPersistent ? {
          duration: Infinity
        } : {})
      });
      if (stackKey) {
        conversationToastsRef.current.set(stackKey, toastId_0);
      }
      if (isPersistent) {
        persistentToastIdsRef.current.add(toastId_0);
      }
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleMessage = t7;
  useWebSocketCallbackSubscription(ALL_ALERT_TYPES, handleMessage, true, "InAppNotifications");
}
function _temp(id) {
  return sonnerToast.dismiss(id);
}